import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { getLanguage, getSelectedExhibitionId, getStepsValidity, selectOrderedStepValidities } from '@app/app.reducer';
import { Store } from '@ngrx/store';
import { GetLocalizedImages } from '@store/customization/customization.actions';
import { StepName } from '@store/step-forms/step.interface';
import { SetSelectedStep } from '@store/step-forms/steps-forms.actions';
import { getLastEnabledPage } from '@store/step-forms/utils/step-validities';
import { mapStepRouteToStepName } from '@store/step-forms/utils/step-validity-mappers';
import { includes, isNil } from 'lodash';
import { Observable, combineLatest, first, map } from 'rxjs';

@Injectable()
export class StepPermissionGuard implements CanActivate {
  readonly #router = inject(Router);
  readonly #store = inject(Store);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return combineLatest({
      stepsValidity: this.#store.select(getStepsValidity),
      orderedStepValidities: this.#store.select(selectOrderedStepValidities),
      selectedExhibitionId: this.#store.select(getSelectedExhibitionId),
      language: this.#store.select(getLanguage)
    }).pipe(
      first(),
      map(({ stepsValidity, orderedStepValidities, selectedExhibitionId, language }) => {
        // get random background image from backend, app component is subscribed
        setTimeout(() => {
          this.#store.dispatch(
            new GetLocalizedImages({
              eventId: Number(selectedExhibitionId),
              countryCode: language
            })
          );
        });

        // get page name (key) from 3rd position in url
        const pageName = state.url.split('/')[3];
        const cleanPageName = pageName.replace(/[?#].*$/, ''); // remove get parameters

        let stepName = mapStepRouteToStepName(cleanPageName) as StepName;

        // grant access to invoice page anytime (we use this guard just to set selected step)
        if (includes([StepName.Invoice, StepName.Recipe], stepName)) {
          return true;
        }

        // on other steps check if all forms of previous steps are valid
        if (!stepsValidity[stepName]?.visible || stepsValidity[stepName]?.disabled) {
          const lastEnabledPage = getLastEnabledPage(orderedStepValidities);

          if (!isNil(lastEnabledPage)) {
            const { key, name } = lastEnabledPage;

            stepName = name;

            const lastValidUrl = state.url.replace(pageName, key);

            this.#router.navigate([lastValidUrl], {
              queryParams: route.queryParams
            });
          }
        }

        this.#store.dispatch(new SetSelectedStep(stepName));

        return true;
      })
    );
  }
}
